import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { CalendarClock, CalendarX2, Loader2 } from 'lucide-react';
import { API } from '../../lib/api';
import { SlotPicker, errText } from './site/DemoDialog';
import { useSeo, Eyebrow, DemoButton } from './site/ui';

const Card = ({ children, testid }) => (
  <div className="mx-auto max-w-2xl rounded-3xl bg-[#ffffff] p-6 shadow-sm sm:p-8" data-testid={testid}>{children}</div>
);

const Cancelled = ({ demo }) => (
  <Card testid="manage-cancelled">
    <CalendarX2 className="text-[#b91c1c]" size={36} />
    <h1 className="dl-display mt-4 text-3xl font-semibold text-[#0f172a]">Demo cancelled</h1>
    <p className="mt-2 text-[15px] text-[#475569]">Your demo on {demo.slot_label} is cancelled. Book a new time whenever you are ready.</p>
    <DemoButton className="mt-6" testid="manage-book-again" />
  </Card>
);

const Current = ({ demo, onCancel, busy }) => (
  <div className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-[#d6d3cb] bg-[#f7f5f0] p-4" data-testid="manage-current">
    <div className="flex items-center gap-3">
      <CalendarClock className="text-[#0f766e]" size={22} />
      <div><p className="text-xs font-semibold text-[#475569]">Booked for</p><p className="font-semibold text-[#0f172a]" data-testid="manage-current-slot">{demo.slot_label}</p></div>
    </div>
    <button type="button" disabled={busy} onClick={onCancel} className="rounded-full border border-[#fecaca] px-4 py-2 text-sm font-semibold text-[#b91c1c] transition-colors hover:bg-[#fef2f2] disabled:opacity-60" data-testid="manage-cancel-btn">Cancel demo</button>
  </div>
);

export default function DemoManage() {
  const { token } = useParams();
  const [demo, setDemo] = useState(null);
  const [days, setDays] = useState([]);
  const [form, setForm] = useState({ demo_slot: '' });
  const [err, setErr] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  useSeo({ title: 'Change your Driveline demo', description: 'Move or cancel your Driveline demo.' });
  const base = `${API}/public/driveline/demo/manage/${token}`;
  const loadSlots = () => axios.get(`${API}/public/driveline/demo-slots`).then((r) => setDays(r.data.days || [])).catch(() => {});
  useEffect(() => {
    axios.get(base).then((r) => setDemo(r.data)).catch((e) => setErr(errText(e)));
    loadSlots();
  }, [base]);

  const act = async (path, body) => {
    setBusy(true); setErr(''); setNote('');
    try {
      const { data } = await axios.post(`${base}/${path}`, body);
      setDemo(data); setForm({ demo_slot: '' }); setConfirmCancel(false);
      if (path === 'reschedule') { setNote(`Done. Your demo is now on ${data.slot_label}. A new calendar invite is on its way.`); loadSlots(); }
    } catch (e) {
      setErr(errText(e));
      if (e?.response?.status === 409) loadSlots();
    } finally { setBusy(false); }
  };

  return (
    <section className="min-h-[70vh] px-5 pb-24 pt-32 sm:px-8" data-testid="demo-manage-page">
      {!demo && !err && <Loader2 className="mx-auto animate-spin text-[#0f766e]" />}
      {!demo && err && <Card testid="manage-invalid"><p className="text-[15px] text-[#b91c1c]">{err}</p><Link to="/driveline" className="mt-4 inline-block text-sm font-semibold text-[#0f766e]">Go to Driveline</Link></Card>}
      {demo?.cancelled && <Cancelled demo={demo} />}
      {demo && !demo.cancelled && (
        <Card testid="manage-active">
          <Eyebrow>Your Driveline demo</Eyebrow>
          <h1 className="dl-display mt-3 text-3xl font-semibold text-[#0f172a] sm:text-4xl">Hi {demo.contact_name}, need another time?</h1>
          <p className="mt-2 text-[15px] text-[#475569]">Pick a new slot for {demo.workshop_name}, or cancel if the demo is no longer needed.</p>
          <div className="mt-6"><Current demo={demo} busy={busy} onCancel={() => setConfirmCancel(true)} /></div>
          {confirmCancel && (
            <div className="mt-3 flex flex-wrap items-center gap-3 rounded-2xl bg-[#fef2f2] p-4 text-sm text-[#7f1d1d]" data-testid="manage-cancel-confirm">
              Cancel this demo?
              <button type="button" disabled={busy} onClick={() => act('cancel')} className="rounded-full bg-[#b91c1c] px-4 py-1.5 font-semibold text-[#ffffff] disabled:opacity-60" data-testid="manage-cancel-yes">Yes, cancel</button>
              <button type="button" onClick={() => setConfirmCancel(false)} className="font-semibold" data-testid="manage-cancel-no">Keep it</button>
            </div>
          )}
          <div className="mt-6"><SlotPicker days={days} form={form} set={(k, v) => setForm({ [k]: v })} /></div>
          {note && <p className="mt-4 rounded-xl border border-[#99f6e4] bg-[#f0fdfa] px-3 py-2 text-sm text-[#134e4a]" data-testid="manage-success">{note}</p>}
          {err && <p className="mt-4 rounded-xl border border-[#fecaca] bg-[#fef2f2] px-3 py-2 text-sm text-[#b91c1c]" data-testid="manage-error">{err}</p>}
          <button type="button" disabled={busy || !form.demo_slot} onClick={() => act('reschedule', form)} className="dl-btn dl-btn-ink mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full px-6 py-3.5 text-[15px] font-semibold disabled:opacity-50" data-testid="manage-reschedule-btn">
            {busy && <Loader2 size={16} className="animate-spin" />} Move my demo
          </button>
        </Card>
      )}
    </section>
  );
}
